import { Loader2, Sparkles } from 'lucide-react'
import type { CompetitorBrief } from '@ispot/shared'

interface InlineBriefProps {
  brief: CompetitorBrief | null
  isLoading: boolean
  canGenerate: boolean
  isEnriching?: boolean
  onGenerate: () => void
}

export function InlineBrief({ brief, isLoading, canGenerate, isEnriching, onGenerate }: InlineBriefProps) {
  if (isLoading || isEnriching) {
    return (
      <div
        style={{
          marginTop: '20px',
          paddingTop: '20px',
          borderTop: '1px solid var(--color-border)',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          color: 'var(--color-text-muted)',
          fontSize: '13px',
          fontFamily: 'var(--font-mono)',
          letterSpacing: '0.02em',
        }}
      >
        <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
        {isEnriching ? 'Enriching competitor…' : 'Generating brief…'}
      </div>
    )
  }

  if (!brief) {
    return (
      <div
        style={{
          marginTop: '20px',
          paddingTop: '20px',
          borderTop: '1px solid var(--color-border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
          flexWrap: 'wrap',
        }}
      >
        <p style={{ margin: 0, fontSize: '13px', color: 'var(--color-text-secondary)', maxWidth: '56ch' }}>
          No brief generated yet. Skerpa can draft strengths, weaknesses, and strategic insights grounded in your uploaded research.
        </p>
        <button
          type="button"
          onClick={onGenerate}
          disabled={!canGenerate}
          style={{
            appearance: 'none',
            font: 'inherit',
            fontSize: '13px',
            background: canGenerate ? 'var(--color-accent)' : 'transparent',
            color: canGenerate ? '#fff' : 'var(--color-text-muted)',
            border: canGenerate ? '1px solid var(--color-accent)' : '1px solid var(--color-border)',
            padding: '7px 14px',
            borderRadius: '6px',
            cursor: canGenerate ? 'pointer' : 'not-allowed',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <Sparkles size={13} />
          Generate brief
        </button>
      </div>
    )
  }

  return (
    <div
      style={{
        marginTop: '20px',
        paddingTop: '20px',
        borderTop: '1px solid var(--color-border)',
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
      }}
    >
      {brief.content.pricing && (
        <div
          style={{
            gridColumn: '1 / -1',
            marginBottom: '22px',
            padding: '0 20px',
          }}
        >
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: '10px',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'var(--color-text-muted)',
              marginBottom: '10px',
            }}
          >
            Pricing
          </div>
          <p
            style={{
              margin: 0,
              fontSize: '13.5px',
              color: 'var(--color-text)',
              lineHeight: 1.55,
              maxWidth: '62ch',
            }}
          >
            {brief.content.pricing}
          </p>
        </div>
      )}
      <BriefColumn label="Strengths" items={brief.content.strengths} />
      <BriefColumn label="Weaknesses" items={brief.content.weaknesses} />
      <BriefColumn label="Insights for Skerpa" items={brief.content.insights} accent isLast />
      <div
        style={{
          gridColumn: '1 / -1',
          marginTop: '22px',
          paddingTop: '14px',
          borderTop: '1px dashed var(--color-border)',
          fontFamily: 'var(--font-mono)',
          fontSize: '11px',
          color: 'var(--color-text-muted)',
          letterSpacing: '0.02em',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexWrap: 'wrap',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: '5px',
            height: '5px',
            borderRadius: '999px',
            background: 'var(--color-market)',
            boxShadow: '0 0 0 3px rgba(0, 180, 190, 0.12)',
          }}
        />
        <span>Generated {new Date(brief.generatedAt).toLocaleString()}</span>
        {brief.modelUsed && (
          <>
            <span style={{ opacity: 0.4 }}>·</span>
            <span>{brief.modelUsed}</span>
          </>
        )}
        <span style={{ opacity: 0.4 }}>·</span>
        <span>Prompt v{brief.promptVersion}</span>
      </div>
    </div>
  )
}

interface BriefColumnProps {
  label: string
  items: string[]
  accent?: boolean
  isLast?: boolean
}

function BriefColumn({ label, items, accent, isLast }: BriefColumnProps) {
  return (
    <div
      style={{
        padding: '0 20px',
        borderRight: isLast ? 'none' : '1px solid var(--color-border)',
      }}
    >
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: '10px',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: 'var(--color-text-muted)',
          marginBottom: '12px',
        }}
      >
        {label}
      </div>
      {items.length === 0 ? (
        <p style={{ margin: 0, fontSize: '13px', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
          None.
        </p>
      ) : (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {items.map((item, i) => (
            <li
              key={i}
              style={{
                fontSize: '13.5px',
                color: 'var(--color-text)',
                lineHeight: 1.5,
                paddingLeft: '14px',
                position: 'relative',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 8,
                  width: 6,
                  height: accent ? 2 : 1,
                  background: accent ? 'var(--color-accent)' : 'var(--color-border-strong)',
                }}
              />
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
